import { useMatch } from "react-router";

import { Timestamp } from "@/shared/components/RobotoMarkdown/Timestamp";
import { Goto } from "@/shared/navigation/Goto";

import { VizExternalLink } from "./VizExternalLink.tsx";
import { VizFileLink } from "./VizFileLink.tsx";
import { VizInternalLink } from "./VizInternalLink.tsx";

interface AISummaryMarkdownLinkProps {
  datasetId?: string;
  fileId?: string;
  topicId?: string;
  messagePathId?: string;
  time?: bigint;
  text: string;
  onLinkClick?: () => void;
}

export const AISummaryMarkdownLink = ({
  datasetId,
  fileId,
  topicId,
  messagePathId,
  time,
  text,
  onLinkClick,
}: AISummaryMarkdownLinkProps) => {
  // Check if we're on the visualizer page. This specific check is much cheaper than subscribing to
  // useViewingContext and works well enough, but can always be replaced in the future.
  const isInVisualizer = useMatch(`${Goto.route.Visualize}/:workspaceId?`);

  // Inside the visualizer, a topic or message path link opens its data in the
  // workspace on screen rather than navigating to a new one.
  if (isInVisualizer && (topicId || messagePathId)) {
    return (
      <VizInternalLink
        messagePathId={messagePathId}
        topicId={topicId}
        text={text}
        time={time}
      />
    );
  }

  // A file or dataset link with a time marks when an event happened on that
  // record. Inside the visualizer it renders as that time, with the Timestamp
  // actions, rather than navigating away from the workspace; VizFileLink also
  // offers to add a file the workspace has not loaded.
  if (isInVisualizer && time !== undefined) {
    if (fileId) {
      return <VizFileLink fileId={fileId} time={time} text={text} />;
    }
    return <Timestamp ns={time}>{text}</Timestamp>;
  }

  return (
    <VizExternalLink
      datasetId={datasetId}
      fileId={fileId}
      topicId={topicId}
      messagePathId={messagePathId}
      text={text}
      time={time}
      onLinkClick={onLinkClick}
    />
  );
};
